import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Loader2, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  emailConnectionApi,
  EmailConnection
} from '../api/emailApi';
import { YahooSMTPModal } from './YahooSMTPModal';

export const EmailIntegration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connection, setConnection] = useState<EmailConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [yahooModalOpen, setYahooModalOpen] = useState(false);
  const [microsoftConnecting, setMicrosoftConnecting] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);

  // Fetch email connection status
  useEffect(() => {
    const fetchConnection = async () => {
      try {
        const data = await emailConnectionApi.getActive();
        setConnection(data as EmailConnection | null);
      } catch (err) {
        console.error('Failed to fetch email connection:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConnection();
  }, []);

  // Handle OAuth success message
  useEffect(() => {
    const provider = searchParams.get('provider');
    const success = searchParams.get('success');

    if (provider && success === 'true') {
      toast.success(`${provider} account connected successfully!`);
      
      // Refresh connection data
      const refetchConnection = async () => {
        const data = await emailConnectionApi.getActive();
        setConnection(data as EmailConnection | null);
      };
      refetchConnection();

      // Clear search params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this email provider?')) {
      return;
    }

    setDisconnecting(true);
    try {
      await emailConnectionApi.disconnect();
      setConnection(null);
      toast.success('Email provider disconnected successfully');
    } catch (err) {
      toast.error('Failed to disconnect email provider');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleMicrosoftConnect = async () => {
    setMicrosoftConnecting(true);
    try {
      const response = await emailConnectionApi.connectMicrosoft();

      if (response.authUrl) {
        window.location.href = response.authUrl;
      }
    } catch (err) {
      setMicrosoftConnecting(false);
      toast.error('Failed to connect to Microsoft. Please try again.');
    }
  };

  const handleGoogleConnect = async () => {
    setGoogleConnecting(true);
    try {
      const response = await emailConnectionApi.connectGoogle();
      if (response.authUrl) {
        window.location.href = response.authUrl;
      }
      
    } catch (err) {
      setGoogleConnecting(false);
      toast.error('Failed to connect to Google. Please try again.');
    }
  };

  const handleYahooSuccess = async () => {
    const data = await emailConnectionApi.getActive();
    setConnection(data as EmailConnection | null);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin mb-2 text-strivre-burgundy" />
        <p>Loading email settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in space-y-6">
      {/* Status Card */}
      <div className="bg-white border border-strivre-lightnavy rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-slate-100 bg-slate-50/30">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-bold text-slate-900">Email Integration</h3>
              <p className="text-sm text-slate-500">Connect email providers to send and receive messages.</p>
            </div>
            <Mail className="h-8 w-8 text-strivre-burgundy" />
          </div>
        </div>

        <div className="p-6">
          {/* Current Status */}
          <div className={`mb-6 p-4 rounded-xl border-2 transition-all ${
              connection
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-center gap-3 text-left">
              {connection ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-green-900">Connected</p>
                    <p className="text-sm text-green-700">
                      {connection.provider === 1
                        ? 'Microsoft' 
                        : connection.provider === 2
                        ? 'Google' 
                        : 'Yahoo'} • {connection.email}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-900">Not Connected</p>
                    <p className="text-sm text-red-700">Connect an email provider to get started</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {connection && (
            <div className="mb-6 flex gap-3">
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-2 px-6 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl font-semibold transition-all disabled:opacity-50 border border-red-200"
              >
                {disconnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                ) : (
                  <Trash2 size={18} />
                )}
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Provider Options - Only show if not connected */}
      {!connection && (
        <div className="bg-white border border-strivre-lightnavy rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-100 bg-slate-50/30">
            <h3 className="text-lg font-bold text-slate-900">Available Providers</h3>
            <p className="text-sm text-slate-500">Choose an email provider to connect</p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Microsoft */}
            <button
              type="button"
              onClick={handleMicrosoftConnect}
              disabled={microsoftConnecting}
              className="
                group p-6 border-2 border-strivre-lightnavy
                hover:border-blue-400 bg-white hover:bg-blue-50
                rounded-xl transition-all text-center
                hover:shadow-md hover:-translate-y-0.5
                disabled:opacity*50 disabled:cursor-not-allowed
              ">
              <div className="flex items-center justify-center h-16 mb-4 rounded-xl">
                {microsoftConnecting ? (
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                ) : (
                  <svg
                    className="w-8 h-8"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path fill="#F35325" d="M1 1h10v10H1z" />
                    <path fill="#81BC06" d="M13 1h10v10H13z" />
                    <path fill="#05A6F0" d="M1 13h10v10H1z" />
                    <path fill="#FFBA08" d="M13 13h10v10H13z" />
                  </svg>
                )}
              </div>

              <p className="font-bold text-slate-900 mb-1">
                {microsoftConnecting
                  ? 'Connecting...'
                  : 'Microsoft'}
              </p>

              <p className="text-xs text-slate-500">
                Outlook & Microsoft 365
              </p>
            </button>

            {/* Google */}
            <button
              type="button"
              onClick={handleGoogleConnect}
              disabled={googleConnecting}
              className="
                p-6
                border-2
                border-strivre-lightnavy
                hover:border-strivre-burgundy
                bg-white
                hover:bg-strivre-lightburgundy
                rounded-xl
                transition-all
                text-center
                group
                hover:shadow-md
                hover:-translate-y-0.5
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              <div className="flex items-center justify-center h-16 mb-4 bg-white group-hover:bg-strivre-lightburgundy rounded-xl transition-all">
                {googleConnecting ? (
                  <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
                ) : (
                  <svg
                    className="w-9 h-9"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      fill="#FFC107"
                      d="M43.6 20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11.5 0 19-8.1 19-19.5 0-1.3-.1-3-.4-4.5z"
                    />

                    <path
                      fill="#FF3D00"
                      d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z"
                    />

                    <path
                      fill="#4CAF50"
                      d="M24 44c5.1 0 9.8-2 13.3-5.2l-6.2-5.2C29.1 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"
                    />

                    <path
                      fill="#1976D2"
                      d="M43.6 20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2C41 35.4 43 30.4 43 24.5c0-1.3-.1-3-.4-4.5z"
                    />
                  </svg>
                )}
              </div>

              <p className="font-bold text-slate-900 mb-1">
                {googleConnecting
                  ? "Connecting..."
                  : "Google"}
              </p>

              <p className="text-xs text-slate-500">
                Gmail & Google Workspace
              </p>
            </button>

            {/* Yahoo */}
            <button
              type="button"
              onClick={() => setYahooModalOpen(true)}
              className="
                group
                p-6
                border-2
                border-strivre-lightnavy
                hover:border-purple-400
                bg-white
                hover:bg-purple-50
                rounded-xl
                transition-all
                text-center
                hover:shadow-md
                hover:-translate-y-0.5
              "
            >
              <div className="flex h-16 items-center justify-center rounded-xl transition-all">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#6001D2] text-white shadow-sm">
                  <span className="text-xl font-black italic tracking-tighter">
                    Y!
                  </span>
                </div>
              </div>

              <p className="mt-4 mb-1 font-bold text-slate-900">
                Yahoo
              </p>

              <p className="text-xs text-slate-500">
                Yahoo Mail
              </p>

              <p className="mt-3 text-[11px] font-medium text-purple-600">
                Manual SMTP configuration
              </p>
            </button>
          </div>

          <div className="px-6 pb-6 text-xs text-slate-500">
            <p>
              💡 <strong>Tip:</strong> Google and Microsoft uses secure OAuth authentication
              and redirects you to Google's login page. Yahoo requires manual
              SMTP configuration.
            </p>
          </div>
        </div>
      )}

      {/* Yahoo Modal */}
      <YahooSMTPModal
        isOpen={yahooModalOpen}
        onClose={() => setYahooModalOpen(false)}
        onSuccess={handleYahooSuccess}
      />

      {/* Info Section */}
      <div className="bg-strivre-lightburgundy border border-strivre-lightnavy rounded-2xl p-6">
        <h4 className="font-bold text-strivre-burgundy mb-3 text-left">ℹ️ Security & Privacy</h4>
        <ul className="space-y-2 text-sm text-slate-700">
          <li className="flex gap-2">
            <span className="flex-shrink-0">✓</span>
            <span>Passwords are encrypted and stored securely</span>
          </li>
          <li className="flex gap-2">
            <span className="flex-shrink-0">✓</span>
            <span>We never store your OAuth tokens in plain text</span>
          </li>
          <li className="flex gap-2">
            <span className="flex-shrink-0">✓</span>
            <span>You can disconnect anytime without losing your data</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
